import { useState } from 'react';
import type { RateDeck } from '@arcnet/contracts';
import { useImportRates, usePublishRateDeck, useRatePreview, useRebuildRetail } from '../../lib/api/hooks-billing.js';
import { ErrorBanner, CardHead, SkeletonBlock, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { formatMoney, formatNumber } from '../../lib/format.js';
import { useRateSheetRead } from '../carriers/useRateSheetRead.js';
import { SheetReadView } from '../carriers/SheetReadView.js';

/**
 * Carrier sheets in, retail rebuilt from them, and nothing published unseen (owner D10, D12;
 * built 29 October 2026). Before this a new carrier sheet was a migration.
 */

/** A cost sheet: the carrier's new file, read and checked, into the draft. */
export function UploadCarrierSheet({ deckId, currency, onDone }: { deckId: string; currency: string; onDone: () => void }) {
  const reader = useRateSheetRead(`/v1/rate-decks/${deckId}/read`, `deck:${deckId}`);
  const load = useImportRates(deckId);
  return (
    <div className="arc-card" style={{ marginBottom: 16 }}>
      <CardHead title="Upload the carrier's sheet"
        sub="A CSV, spreadsheet or PDF. It replaces the draft's rates; nothing is charged differently until the draft is published." />
      <div className="arc-card-body">
        <div className="arc-row" style={{ gap: 8 }}>
          <label className="arc-btn arc-btn-sm" style={{ cursor: 'pointer' }}>
            <Icon name="upload" size={13} /> Choose a file
            <input type="file" accept=".csv,.txt,.xlsx,.xls,.pdf" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void reader.read(f); e.target.value = ''; }} />
          </label>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={onDone}>Close</button>
        </div>
        <SheetReadView reader={reader} currency={currency}
          useLabel={reader.ok ? `Put ${formatNumber(reader.ok.rules.length)} rates into the draft` : 'Put into the draft'}
          pending={load.isPending} error={load.error}
          onUse={() => {
            const rules = reader.ok!.rules.map((r) => ({
              prefix: r.prefix, ...(r.description ? { description: r.description } : {}),
              rate_per_minute: r.rate_per_minute, connection_fee: r.connection_fee,
              first_increment_sec: r.first_increment_sec, subsequent_increment_sec: r.subsequent_increment_sec,
            }));
            load.mutate(rules, { onSuccess: () => { reader.reset(); onDone(); } });
          }} />
      </div>
    </div>
  );
}

/** A sell sheet: its draft rebuilt from a carrier's cost at the markup. */
export function RebuildRetail({ deckId, currency, decks, onDone }: {
  deckId: string; currency: string; decks: RateDeck[]; onDone: () => void;
}) {
  const costs = decks.filter((d) => d.deck_type === 'cost' && d.currency === currency);
  const [from, setFrom] = useState(costs[0]?.id ?? '');
  const [markup, setMarkup] = useState('30');
  const rebuild = useRebuildRetail(deckId);
  const pct = Number(markup);
  const valid = !!from && Number.isFinite(pct) && pct >= 0 && pct <= 500;
  return (
    <div className="arc-card" style={{ marginBottom: 16 }}>
      <CardHead title="Rebuild from the carrier's cost"
        sub="Every destination the carrier prices, at its cost plus the markup, rounded up to the cent, billed in the carrier's increments. The result replaces the draft." />
      <div className="arc-card-body">
        {costs.length === 0 ? (
          <p className="arc-muted" style={{ margin: 0 }}>There is no {currency} cost sheet to build from.</p>
        ) : (
          <form className="arc-row" style={{ gap: 12, alignItems: 'end', flexWrap: 'wrap' }}
            onSubmit={(e) => { e.preventDefault(); if (valid) rebuild.mutate({ from_deck_id: from, markup_pct: pct }, { onSuccess: onDone }); }}>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="rb-from">Carrier cost sheet</label>
              <select id="rb-from" className="arc-select" value={from} onChange={(e) => setFrom(e.target.value)}>
                {costs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="arc-field" style={{ margin: 0, width: 120 }}>
              <label className="arc-label" htmlFor="rb-markup">Markup</label>
              <div className="arc-row" style={{ gap: 6 }}>
                <input id="rb-markup" className="arc-input arc-num" inputMode="decimal" value={markup}
                  onChange={(e) => setMarkup(e.target.value)} aria-describedby="rb-markup-hint" />
                <span>%</span>
              </div>
            </div>
            <button type="submit" className="arc-btn arc-btn-primary" disabled={!valid || rebuild.isPending}>
              {rebuild.isPending ? 'Rebuilding…' : 'Rebuild the draft'}
            </button>
            <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
          </form>
        )}
        <p id="rb-markup-hint" className="arc-subtle" style={{ fontSize: 12, margin: '8px 0 0' }}>
          30% is the platform's retail markup. The carrier's draft is used when it has one, so a new sheet can be priced before it goes live.
        </p>
        {rebuild.error && <ErrorBanner error={rebuild.error} />}
      </div>
    </div>
  );
}

const KIND_LABEL: Record<string, string> = { added: 'New', removed: 'Removed', raised: 'Up', lowered: 'Down', terms: 'Terms' };

/**
 * What the draft would change, before it can be published (doc 15 section 3): the rules added,
 * removed, raised and lowered; the last 30 days of calls re-priced both ways; and every destination
 * that would sell below its cost. Publishing sends this preview's checksum, so a draft edited after
 * the preview was opened has to be looked at again.
 */
export function PublishReview({ deckId, onClose }: { deckId: string; onClose: () => void }) {
  const preview = useRatePreview(deckId, true);
  const publish = usePublishRateDeck(deckId);
  const step = useStepUp();
  const [all, setAll] = useState(false);
  const p = preview.data;
  const sell = p?.deck.deck_type === 'sell';
  const diff = p ? Number(p.impact.difference) : 0;
  const money = (v: string) => formatMoney(v, p?.impact.currency ?? 'ZAR');

  return (
    <div className="arc-card" style={{ marginBottom: 16 }}>
      <CardHead title={p ? `Review draft v${p.draft_version} before publishing` : 'Review the draft'}
        sub={p ? (p.live_version ? `Compared with v${p.live_version}, the version in force.` : 'Nothing is in force yet.') : undefined} />
      <div className="arc-card-body arc-stack" style={{ gap: 14 }}>
        {!p ? <SkeletonBlock height={120} query={preview} /> : (
          <>
            <div className="arc-row" style={{ gap: 18, flexWrap: 'wrap' }}>
              {([['New', p.counts.added], ['Removed', p.counts.removed], ['Up', p.counts.raised], ['Down', p.counts.lowered],
                ['Terms changed', p.counts.terms], ['Unchanged', p.counts.unchanged]] as const).map(([label, n]) => (
                <div key={label}>
                  <div className="arc-stat-label">{label}</div>
                  <div className="arc-stat-value arc-num" style={{ fontSize: 22 }}>{formatNumber(n)}</div>
                </div>
              ))}
            </div>

            <div>
              <strong style={{ fontSize: 13 }}>{sell ? 'Revenue' : 'Cost'}, the last {p.impact.days} days re-priced</strong>
              {p.impact.calls === 0 ? (
                <p className="arc-muted" style={{ margin: '4px 0 0', fontSize: 13 }}>No calls in the last {p.impact.days} days were priced by this sheet.</p>
              ) : (
                <>
                  <p style={{ margin: '4px 0 6px', fontSize: 13 }}>
                    {formatNumber(p.impact.calls)} calls: {money(p.impact.before)} now, {money(p.impact.after)} with the draft —{' '}
                    <strong style={{ color: diff === 0 ? undefined : (sell ? diff < 0 : diff > 0) ? 'var(--arc-danger)' : 'var(--arc-ok)' }}>
                      {diff > 0 ? '+' : ''}{money(p.impact.difference)}
                    </strong>
                    {sell ? (diff < 0 ? ' less revenue.' : diff > 0 ? ' more revenue.' : '.') : (diff > 0 ? ' more cost.' : diff < 0 ? ' less cost.' : '.')}
                    {p.impact.unrated > 0 && <span className="arc-subtle"> {formatNumber(p.impact.unrated)} calls would have no rate under the draft.</span>}
                  </p>
                  {p.impact.by_destination.length > 0 && (
                    <div className="arc-table-wrap">
                      <table className="arc-table">
                        <thead><tr><th>Destination</th><th className="arc-td-right">Calls</th><th className="arc-td-right">Minutes</th>
                          <th className="arc-td-right">Now</th><th className="arc-td-right">Draft</th><th className="arc-td-right">Change</th></tr></thead>
                        <tbody>
                          {p.impact.by_destination.map((d) => (
                            <tr key={d.prefix}>
                              <td><span className="arc-mono">+{d.prefix}</span> {d.description ?? ''}</td>
                              <td className="arc-td-right arc-num">{formatNumber(d.calls)}</td>
                              <td className="arc-td-right arc-num">{formatNumber(d.minutes)}</td>
                              <td className="arc-td-right arc-num">{money(d.before)}</td>
                              <td className="arc-td-right arc-num">{money(d.after)}</td>
                              <td className="arc-td-right arc-num">{Number(d.difference) > 0 ? '+' : ''}{money(d.difference)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            {p.below_cost.count > 0 && (
              <div className="arc-banner arc-banner-danger" role="alert">
                <Icon name="warning" size={14} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>
                    {formatNumber(p.below_cost.count)} destination{p.below_cost.count === 1 ? '' : 's'} would sell below cost
                  </div>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12.5 }}>
                    {p.below_cost.rules.slice(0, 8).map((b) => (
                      <li key={`${b.prefix}-${b.cost_deck}`}>
                        +{b.prefix} {b.description ?? ''}: sells at {money(b.sell)}, costs {money(b.cost)} ({b.cost_deck})
                      </li>
                    ))}
                    {p.below_cost.count > 8 && <li>and {formatNumber(p.below_cost.count - 8)} more</li>}
                  </ul>
                </div>
              </div>
            )}

            {p.changes.length > 0 && (
              <div>
                <strong style={{ fontSize: 13 }}>Biggest changes</strong>
                <div className="arc-table-wrap" style={{ marginTop: 6 }}>
                  <table className="arc-table">
                    <thead><tr><th>Prefix</th><th>Destination</th><th /><th className="arc-td-right">Now</th><th className="arc-td-right">Draft</th></tr></thead>
                    <tbody>
                      {(all ? p.changes : p.changes.slice(0, 12)).map((c) => (
                        <tr key={`${c.prefix}-${c.time_band}`}>
                          <td className="arc-mono">+{c.prefix}{c.time_band !== 'anytime' ? ` (${c.time_band})` : ''}</td>
                          <td>{c.description ?? '—'}</td>
                          <td><Badge tone={c.kind === 'raised' || c.kind === 'added' ? 'info' : c.kind === 'removed' || c.kind === 'lowered' ? 'warn' : 'muted'}>{KIND_LABEL[c.kind]}</Badge></td>
                          <td className="arc-td-right arc-num">{c.old_rate === null ? '—' : money(c.old_rate)}</td>
                          <td className="arc-td-right arc-num">{c.new_rate === null ? '—' : money(c.new_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {p.changes.length > 12 && (
                  <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" style={{ marginTop: 6 }} onClick={() => setAll((v) => !v)}>
                    {all ? 'Show fewer' : `Show ${p.changes.length >= 200 ? 'the 200 biggest' : `all ${p.changes.length}`}`}
                  </button>
                )}
              </div>
            )}

            {publish.error && <ErrorBanner error={publish.error} />}
            <div className="arc-row" style={{ gap: 8 }}>
              <button type="button" className="arc-btn arc-btn-primary" disabled={publish.isPending || step.busy}
                onClick={async () => {
                  if (!(await confirmAction({
                    title: `Publish v${p.draft_version}?`,
                    body: p.below_cost.count > 0
                      ? `Calls are priced by it from now on, and ${p.below_cost.count} destination${p.below_cost.count === 1 ? '' : 's'} would sell below cost.`
                      : 'Calls are priced by it from now on.',
                    action: 'Publish',
                    danger: p.below_cost.count > 0,
                  }))) return;
                  void step.run(() => publish.mutateAsync(p.checksum)).then(onClose);
                }}>
                {publish.isPending || step.busy ? 'Publishing…' : `Publish v${p.draft_version}`}
              </button>
              <button type="button" className="arc-btn" onClick={onClose}>Not now</button>
            </div>
            <StepUpPrompt step={step} what="Publishing a rate sheet">
              From the moment it is published, every call to these destinations is charged at these rates.
            </StepUpPrompt>
          </>
        )}
      </div>
    </div>
  );
}
